import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, ChevronsLeft, ChevronsRight, Globe, LogOut, Menu, Search, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useApi } from '../contexts/ApiContext';
import { activeHref, NavGroup, useVisibleNav } from '../lib/navigation';
import VersionTag from './VersionTag';
import ThemeToggle from './ThemeToggle';
import NotificationBell from './NotificationBell';
import CommandPalette, { PALETTE_SHORTCUT } from './CommandPalette';

const SIDEBAR_KEY = 'sidebar-collapsed';

function getInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Grouped nav links. Collapsed: icons only, groups separated by a rule. */
function NavList({ groups, current, collapsed, onNavigate }: { groups: NavGroup[]; current: string | null; collapsed: boolean; onNavigate?: () => void }) {
  return (
    <nav className={`flex-1 overflow-y-auto py-2 ${collapsed ? 'px-2' : 'px-3'}`}>
      {groups.map((g, gi) => (
        <div key={g.label ?? '_'} className={gi > 0 ? 'mt-4' : ''}>
          {g.label && (collapsed
            ? <div className="mx-2 mb-2 border-t border-ink-700/60" />
            : <div className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-500">{g.label}</div>)}
          <div className="space-y-0.5">
            {g.items.map((item) => {
              const isActive = current === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onNavigate}
                  title={collapsed ? item.name : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  className={`relative flex items-center rounded-lg py-1.5 text-sm font-medium transition-colors duration-150 ${
                    collapsed ? 'justify-center px-0' : 'gap-3 px-3'
                  } ${isActive ? 'bg-[#4f46e5]/25 text-[#e0e7ff]' : 'text-ink-300 hover:bg-ink-800 hover:text-white'}`}
                >
                  {isActive && !collapsed && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-[#818cf8]" />}
                  <item.icon className={`h-[18px] w-[18px] shrink-0 ${isActive ? 'text-[#a5b4fc]' : 'text-ink-500'}`} />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function SearchButton({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  if (collapsed) {
    return (
      <button onClick={onClick} title={`Search (${PALETTE_SHORTCUT})`}
        className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg text-ink-400 hover:bg-ink-800 hover:text-white">
        <Search className="h-[18px] w-[18px]" />
      </button>
    );
  }
  return (
    <button onClick={onClick}
      className="mx-3 flex w-[calc(100%-1.5rem)] items-center gap-2 rounded-lg border border-ink-700/70 bg-ink-850 px-3 py-1.5 text-sm text-ink-400 transition-colors hover:border-ink-600 hover:text-ink-300">
      <Search className="h-4 w-4" />
      <span className="flex-1 text-left">Search…</span>
      <kbd className="rounded border border-ink-700 px-1.5 text-[10px] font-medium text-ink-500">{PALETTE_SHORTCUT}</kbd>
    </button>
  );
}

function Brand({ collapsed }: { collapsed: boolean }) {
  return (
    <Link to="/" className={`flex h-14 shrink-0 items-center ${collapsed ? 'justify-center px-2' : 'gap-2.5 px-5'}`}>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#6366f1] to-[#4338ca] shadow-sm shadow-black/30">
        <Globe className="h-4 w-4 text-white" />
      </div>
      {!collapsed && <span className="whitespace-nowrap text-lg font-semibold tracking-tight text-white">DomainHQ</span>}
    </Link>
  );
}

function UserRow({ collapsed }: { collapsed: boolean }) {
  const { user } = useApi();
  if (!user) return null;
  const name = user.display_name || user.username;
  const avatar = user.avatar_url
    ? <img src={user.avatar_url} alt="" className={`${collapsed ? 'h-7 w-7' : 'h-6 w-6'} shrink-0 rounded-full`} referrerPolicy="no-referrer" />
    : (
      <div className={`${collapsed ? 'h-7 w-7' : 'h-6 w-6'} flex shrink-0 items-center justify-center rounded-full bg-ink-700`}>
        <User className="h-3.5 w-3.5 text-ink-400" />
      </div>
    );
  return collapsed
    ? <div className="flex justify-center py-2" title={name}>{avatar}</div>
    : <div className="flex items-center gap-2 px-3 py-2">{avatar}<span className="truncate text-sm text-ink-400">{name}</span></div>;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useApi();
  const groups = useVisibleNav();
  const current = activeHref(location.pathname, groups);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_KEY, String(collapsed)); } catch { /* ignore */ }
  }, [collapsed]);

  // ⌘K / Ctrl+K opens the command palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const footerBtn = (isCollapsed: boolean) =>
    `flex w-full items-center rounded-lg py-2 text-sm font-medium text-ink-400 transition-colors hover:bg-ink-800 hover:text-white ${isCollapsed ? 'justify-center px-0' : 'gap-3 px-3'}`;

  const sidebarWidth = collapsed ? 'w-[68px]' : 'w-64';
  const mainPadding = collapsed ? 'lg:pl-[68px]' : 'lg:pl-64';

  return (
    <div className="min-h-screen bg-slate-50" style={{ '--sidebar-w': collapsed ? '68px' : '16rem' } as React.CSSProperties}>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} onLogout={handleLogout} />

      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-ink-950/60 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-ink-900 shadow-xl">
          <div className="flex items-center justify-between pr-4">
            <Brand collapsed={false} />
            <button onClick={() => setSidebarOpen(false)} className="text-ink-400 transition-colors hover:text-white" aria-label="Close menu">
              <X className="h-5 w-5" />
            </button>
          </div>
          <SearchButton collapsed={false} onClick={() => { setSidebarOpen(false); setPaletteOpen(true); }} />
          <NavList groups={groups} current={current} collapsed={false} onNavigate={() => setSidebarOpen(false)} />
          <div className="space-y-1 border-t border-ink-700/50 p-3">
            <UserRow collapsed={false} />
            <ThemeToggle />
            <button onClick={handleLogout} className={footerBtn(false)}>
              <LogOut className="h-[18px] w-[18px] shrink-0" />
              Sign Out
            </button>
            <VersionTag />
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col ${sidebarWidth} z-30 transition-all duration-200 ease-in-out`}>
        <div className="flex flex-1 flex-col overflow-hidden border-r border-ink-800 bg-ink-900">
          <Brand collapsed={collapsed} />
          <SearchButton collapsed={collapsed} onClick={() => setPaletteOpen(true)} />
          <div className={`mt-2 ${collapsed ? 'px-2' : 'px-3'}`}><NotificationBell collapsed={collapsed} /></div>
          <NavList groups={groups} current={current} collapsed={collapsed} />
          <div className="space-y-1 border-t border-ink-700/50 p-2">
            <UserRow collapsed={collapsed} />
            <ThemeToggle collapsed={collapsed} />
            <button onClick={handleLogout} title={collapsed ? 'Sign Out' : undefined} className={footerBtn(collapsed)}>
              <LogOut className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && <span>Sign Out</span>}
            </button>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className={`${footerBtn(collapsed)} text-ink-500 hover:text-ink-300`}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronsRight className="h-[18px] w-[18px] shrink-0" /> : (
                <>
                  <ChevronsLeft className="h-[18px] w-[18px] shrink-0" />
                  <span>Collapse</span>
                </>
              )}
            </button>
            <VersionTag collapsed={collapsed} />
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`${mainPadding} transition-all duration-200 ease-in-out`}>
        <div className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-slate-200 bg-surface/80 px-4 backdrop-blur-md lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="text-slate-500 hover:text-slate-700" aria-label="Open menu">
            <Menu className="h-5 w-5" />
          </button>
          <span className="flex-1 text-base font-semibold text-slate-900">DomainHQ</span>
          <NotificationBell variant="topbar" />
          <button onClick={() => setPaletteOpen(true)} className="text-slate-500 hover:text-slate-700" aria-label="Search">
            <Search className="h-5 w-5" />
          </button>
        </div>
        <main className="p-4 lg:p-8">{children}</main>
        <footer className="flex items-center justify-between border-t border-slate-200 px-4 py-4 text-xs text-slate-400 lg:px-8">
          <span>DomainHQ</span>
          <a href="/api/swagger/ui" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-slate-400 transition-colors hover:text-brand-600">
            <BookOpen className="h-3.5 w-3.5" />
            API Reference
          </a>
        </footer>
      </div>
    </div>
  );
}
